import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Pencil, Eye } from 'lucide-react';

const EmailTemplatesSection = ({
  emailTemplates,
  editingTemplate,
  setEditingTemplate,
  showTemplateDialog,
  setShowTemplateDialog,
  showPreviewDialog,
  setShowPreviewDialog,
  previewTemplate,
  handleInitializeTemplates,
  handleEditTemplate,
  handleSaveTemplate,
  handlePreviewTemplate,
  configuredLanguages
}) => {
  const templateTypes = {
    payment_confirmation: 'Payment Confirmation',
    price_update: 'Price Update Notification'
  };

  // Build languages map from configured languages
  const languages = {};
  const configuredLanguageCodes = new Set();
  configuredLanguages.forEach(lang => {
    languages[lang.code] = lang.name;
    configuredLanguageCodes.add(lang.code);
  });

  // Filter templates to only show those for configured languages
  const filteredTemplates = emailTemplates.filter(template => 
    configuredLanguageCodes.has(template.language)
  );

  const groupedTemplates = filteredTemplates.reduce((acc, template) => {
    if (!acc[template.template_type]) {
      acc[template.template_type] = [];
    }
    acc[template.template_type].push(template);
    return acc;
  }, {});

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle>Email Templates</CardTitle>
            <CardDescription>Manage email templates in multiple languages</CardDescription>
          </div>
          <div className="flex gap-2">
            {configuredLanguages.length > 0 && (
              <Button onClick={handleInitializeTemplates} variant="outline">
                Sync Templates
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {configuredLanguages.length > 0 && (
          <div className="mb-6 bg-gray-50 border rounded-lg p-4">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Configured Languages:</span>{' '}
                  {configuredLanguages.map((lang, idx) => {
                    const hasTemplates = filteredTemplates.some(t => t.language === lang.code);
                    return (
                      <span key={lang.code} className={!hasTemplates ? 'text-amber-600 font-medium' : ''}>
                        {lang.name} ({lang.code}){hasTemplates ? '' : ' ⚠️'}{idx < configuredLanguages.length - 1 ? ', ' : ''}
                      </span>
                    );
                  })}
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  {filteredTemplates.length < configuredLanguages.length * 2 ? (
                    <span className="text-amber-600">⚠️ Some templates are missing. Click "Sync Templates" to create them.</span>
                  ) : (
                    <span>All templates created. To add more languages, go to Country Requirements.</span>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {configuredLanguages.length === 0 ? (
          <div className="text-center py-12">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 max-w-2xl mx-auto">
              <h3 className="font-semibold text-amber-900 mb-3">⚠️ No Languages Configured</h3>
              <p className="text-amber-800 mb-4">
                Before initializing email templates, you need to add countries with their languages in the <strong>Country Requirements</strong> section.
              </p>
              <p className="text-sm text-amber-700">
                Each country you add will define a language (e.g., USA → English, Spain → Spanish). Email templates will be created for those languages automatically.
              </p>
            </div>
          </div>
        ) : filteredTemplates.length === 0 && emailTemplates.length === 0 ? (
          <div className="text-center py-12">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-2xl mx-auto">
              <h3 className="font-semibold text-blue-900 mb-3">📧 Ready to Create Templates</h3>
              <p className="text-blue-800 mb-4">
                You have {configuredLanguages.length} language(s) configured: <strong>{configuredLanguages.map(l => l.name).join(', ')}</strong>
              </p>
              <p className="text-sm text-blue-700 mb-4">
                Click "Sync Templates" button above to create email templates for these languages.
              </p>
            </div>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="text-center py-12">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 max-w-2xl mx-auto">
              <h3 className="font-semibold text-amber-900 mb-3">⚠️ Templates Missing for Configured Languages</h3>
              <p className="text-amber-800 mb-4">
                You have {configuredLanguages.length} language(s) configured: <strong>{configuredLanguages.map(l => l.name).join(', ')}</strong>
              </p>
              <p className="text-sm text-amber-700 mb-4">
                But no templates exist for these languages. Click "Sync Templates" button above to create them.
              </p>
              {emailTemplates.length > 0 && (
                <p className="text-xs text-amber-600 mt-2">
                  Note: {emailTemplates.length} template(s) exist for other languages not currently configured.
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedTemplates).map(([type, templates]) => (
              <div key={type} className="border rounded-lg p-4">
                <h3 className="text-lg font-semibold mb-4">{templateTypes[type]}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {templates.map((template) => (
                    <div key={template.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-sm">{languages[template.language]}</span>
                        <span className="text-xs bg-gray-100 px-2 py-1 rounded">{template.language}</span>
                      </div>
                      <p className="text-xs text-gray-600 mb-3 truncate">{template.subject}</p>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditTemplate(template)}
                          className="flex-1"
                        >
                          <Pencil className="w-3 h-3 mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handlePreviewTemplate(template)}
                          className="flex-1"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          Preview
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
              <h4 className="font-semibold text-blue-900 mb-2">Available Template Variables</h4>
              <div className="text-sm text-blue-800 space-y-1">
                <p><code className="bg-blue-100 px-2 py-1 rounded">{'{{user_name}}'}</code> - Customer's name</p>
                <p><code className="bg-blue-100 px-2 py-1 rounded">{'{{package_name}}'}</code> - Package name</p>
                <p><code className="bg-blue-100 px-2 py-1 rounded">{'{{amount}}'}</code> - Amount</p>
                <p><code className="bg-blue-100 px-2 py-1 rounded">{'{{currency}}'}</code> - Currency (USD, EUR, etc.)</p>
                <p><code className="bg-blue-100 px-2 py-1 rounded">{'{{billing_cycle}}'}</code> - monthly/yearly</p>
                <p><code className="bg-blue-100 px-2 py-1 rounded">{'{{old_price}}'}</code> - Old price (for price updates)</p>
                <p><code className="bg-blue-100 px-2 py-1 rounded">{'{{new_price}}'}</code> - New price (for price updates)</p>
                <p><code className="bg-blue-100 px-2 py-1 rounded">{'{{company_name}}'}</code> - Your company name</p>
              </div>
            </div>
          </div>
        )}
      </CardContent>

      {/* Edit Template Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Edit {editingTemplate && languages[editingTemplate.language]} Template
            </DialogTitle>
            <DialogDescription>
              {editingTemplate && templateTypes[editingTemplate.template_type]}
            </DialogDescription>
          </DialogHeader>
          {editingTemplate && (
            <div className="space-y-4 mt-4">
              <div>
                <Label>Subject Line</Label>
                <Input
                  value={editingTemplate.subject}
                  onChange={(e) => setEditingTemplate({
                    ...editingTemplate,
                    subject: e.target.value
                  })}
                  placeholder="Email subject"
                />
              </div>
              <div>
                <Label>Email Body (HTML)</Label>
                <textarea
                  className="w-full h-96 p-3 border rounded-lg font-mono text-sm"
                  value={editingTemplate.body}
                  onChange={(e) => setEditingTemplate({
                    ...editingTemplate,
                    body: e.target.value
                  })}
                  placeholder="HTML email body"
                />
                <p className="text-xs text-gray-500 mt-1">
                  You can edit the HTML directly. Use template variables like {'{{user_name}}'}, {'{{package_name}}'}, etc.
                </p>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveTemplate}>
                  Save Template
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Email Preview</DialogTitle>
            <DialogDescription>
              Preview with sample data
            </DialogDescription>
          </DialogHeader>
          {previewTemplate && (
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-sm font-semibold">Subject:</Label>
                <p className="mt-1 p-3 bg-gray-50 rounded border">{previewTemplate.subject}</p>
              </div>
              <div>
                <Label className="text-sm font-semibold">Body:</Label>
                <div 
                  className="mt-1 p-4 bg-white rounded border"
                  dangerouslySetInnerHTML={{ __html: previewTemplate.body }}
                />
              </div>
              <div className="flex justify-end">
                <Button onClick={() => setShowPreviewDialog(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default EmailTemplatesSection;
